/**
 * PersistentDashboardMap — mounts once at MainLayout level and portals the fleet map into
 * whatever DOM node DashboardPage publishes via DashboardMapAnchorContext.
 *
 * When DashboardPage unmounts (user leaves the dashboard), the anchor becomes null and this
 * component returns null, which unmounts <MapView>. #369 — the google.maps.Map itself is kept by the map library
 * (reuseMaps) and handed back to the next mount: no new map load, no rebuild (the Maps JS API itself is loaded
 * once). On that next mount the library re-applies the options from the props, centre and zoom included, so
 * #290 carries the camera: the centre and zoom are saved in this tab (lib/dashboardMapView) and restored.
 *
 * #363 — when a group the user can see has a site drawing, the box gets a Google map | Site map bar. "Site map"
 * unmounts <MapView> the same way leaving the page does and mounts <SiteMapView> (the drawing with hand-placed pins)
 * in the same box, with the same pins, filter, alert states, selection and pin click. The choice is kept in this
 * tab like the map camera. With no drawing, nothing here changes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import * as Dialog from '@radix-ui/react-dialog'
import { Loader2 } from 'lucide-react'
import { camerasApi, sitePlansApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { useImageRetry } from '@/hooks/useImageRetry'  // #336
import { useMapSettings } from '@/hooks/useMapSettings'
import { wedgeColour, wedgeDefaults } from '@/lib/mapSettings'
import { MapView, type DirectionDraft, type MapMarker } from '@/components/map/MapView'
import { MapSettingsButton } from '@/components/maps/MapSettingsDialog'
import { MapModeSwitch } from '@/components/sitemap/MapModeSwitch'
import { SiteMapView } from '@/components/sitemap/SiteMapView'
import { DirectionFields } from '@/components/maps/DirectionFields'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Button } from '@/components/ui/button'
import { useDashboardMapAnchor } from '@/contexts/DashboardMapAnchorContext'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import { useCriticalAlertsContext } from '@/components/layout/CriticalAlertsContext'
import type { DetectionNotification } from '@/components/notifications/shared'
import { useAuthStore } from '@/store/authStore'
import { useDirectionPreview } from '@/store/directionPreview'
import { loadMapMode, loadMapView, saveMapCamera, saveMapMode } from '@/lib/dashboardMapView'
import { toast } from '@/lib/toast'
import { directionForm, parseDirection, type DirectionForm, type DirectionValues } from '@/lib/mapDirection'
import type { SitePlanSummary } from '@/types'

/** #336 (review M1) — two markers of one camera are the same but for the thumbnail link. #330 — the facing wedge
 *  (heading, width, range) counts: a saved direction makes a new marker, so the wedge is drawn again. */
function sameExceptLink(a: MapMarker, b: MapMarker): boolean {
  const groups = (m: MapMarker) => m.groupNames ?? []
  return a.id === b.id && Object.is(a.lat, b.lat) && Object.is(a.lng, b.lng) && a.label === b.label
    && a.sublabel === b.sublabel && a.address === b.address && a.pulse === b.pulse && a.isOnline === b.isOnline
    && a.lastEventType === b.lastEventType && a.lastEventAt === b.lastEventAt
    && a.heading === b.heading && a.fov === b.fov && a.rangeM === b.rangeM
    && groups(a).length === groups(b).length && groups(a).every((g, i) => g === groups(b)[i])
}

// #279 — a drop waiting for the superuser's answer in the confirm box.
interface PendingMove {
  id: number
  name: string
  lat: number
  lng: number
  resolve: (accepted: boolean) => void
}

const NO_SITE_PLANS: SitePlanSummary[] = []

// The API stores coordinates with 6 decimals; send exactly that so what is saved is what was shown.
const coord = (n: number) => n.toFixed(6)

/** #306 — how long a camera's pin alerts after its alert arrives (owner 2026-10-12: "Fixed 60 seconds"). */
export const PIN_ALERT_MS = 60_000

/** #306 — the cameras whose pins alert at `now`, each with the time its newest alert in the list arrived,
 *  and when the earliest 60 s window still running ends (Infinity: none runs). A camera alerts while one
 *  of its alerts is less than PIN_ALERT_MS old or still has its card on screen (in `peekIds`); the time
 *  is its newest alert whichever one keeps it on, so an older held card never brings back an older time
 *  (that would bounce the pin again with no new alert — 306 review). */
function alertingCameras(notifications: DetectionNotification[], peekIds: string[], now: number) {
  const onScreen = new Set(peekIds)
  const newest = new Map<number, number>()
  const on = new Set<number>()
  let nextEnd = Infinity
  for (const n of notifications) {
    const id = (n.event as { camera_id?: unknown } | null)?.camera_id
    if (typeof id !== 'number') continue
    newest.set(id, Math.max(newest.get(id) ?? n.createdAt, n.createdAt))
    const end = n.createdAt + PIN_ALERT_MS
    if (end > now) nextEnd = Math.min(nextEnd, end)
    if (end > now || onScreen.has(n.id)) on.add(id)
  }
  const since = new Map([...on].map((id) => [id, newest.get(id) as number]))
  return { since, nextEnd }
}

/** #330 — the confirm box after a direction is picked on the map: the camera's name, the heading (with the
 *  compass presets), the width and the range, prefilled; Save sends all three. Values out of range keep Save off
 *  and say why. Round 2 — each edit that makes the three valid moves the wedge behind the box at once (`onPreview`:
 *  heading, width and range); text that is not valid leaves the last valid wedge drawn. #339 — the heading is
 *  required; an empty width or range follows the map default (its placeholder) and is sent as null, so the box
 *  never writes the default into the camera. */
function DirectionDialog({ name, initial, saving, onPreview, onSave, onCancel }: {
  name: string
  initial: DirectionForm
  saving: boolean
  onPreview: (values: DirectionValues) => void
  onSave: (values: DirectionValues) => void
  onCancel: () => void
}) {
  const { t } = useTranslation('cameras')
  // Fix round 2 — the compass in the configured wedge colour, from the map config the map behind has loaded
  // (useMapSettings reads the cached config with the defaults and registers no query of its own, #287); #339 — and
  // the wedge defaults an empty width or range follows
  const settings = useMapSettings()
  const colour = wedgeColour(settings)
  const defaults = wedgeDefaults(settings)
  const [form, setForm] = useState(initial)
  const parse = (f: DirectionForm) => parseDirection(f, { heading: true })   // #339 — the heading only
  const values = parse(form)
  const change = (next: DirectionForm) => {
    setForm(next)
    const valid = parse(next)
    if (valid) onPreview(valid)
  }
  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open && !saving) onCancel() }}>
      <Dialog.Portal>
        {/* Lighter than the #279 box, and no blur: the wedge being set stays visible behind it */}
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-5 shadow-2xl"
        >
          <Dialog.Title className="text-base font-semibold">{t('map.direction.title')}</Dialog.Title>
          <p className="mt-0.5 break-words text-sm text-muted-foreground">{name}</p>
          <form
            className="mt-4 space-y-4"
            onSubmit={(e) => { e.preventDefault(); if (values && !saving) onSave(values) }}
          >
            <DirectionFields value={form} onChange={change} defaults={defaults} disabled={saving} required colour={colour} />
            {!values && <p role="alert" className="text-xs text-destructive">{t('edit.directionInvalid')}</p>}
            <div className="flex gap-3 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
                {t('map.direction.cancel')}
              </Button>
              <Button type="submit" className="flex-1" disabled={saving || !values}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('map.direction.save')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function PersistentDashboardMap() {
  const { anchor, modeAnchor, onMarkerClick, visibleIds, selectedId, filterEdits } = useDashboardMapAnchor()
  // #363 — the groups with a site drawing. Only the list itself redraws the map: a pending or failed request does not.
  const { data: sitePlans = NO_SITE_PLANS } = useQuery({
    queryKey: queryKeys.sitePlans.list(),
    queryFn: () => sitePlansApi.list(),
    ...queryOptions.sitePlans.list,
    enabled: !!anchor,
    notifyOnChangeProps: ['data'],
  })
  // #363 — Google map or Site map, as this tab last left it (lib/dashboardMapView; login and logout reset it).
  const [mapMode, setMapMode] = useState(loadMapMode)
  // The drawing the site map shows (or opens on): the chosen one while it exists, else the first; none = Google map.
  const siteTarget = sitePlans.some((p) => p.group_id === mapMode.groupId) ? mapMode.groupId : sitePlans[0]?.group_id ?? null
  const siteGroupId = mapMode.view === 'site' ? siteTarget : null
  const siteShown = siteGroupId !== null
  // #363 review N4 — pins being arranged on the site map hold the switch still (unsaved moves)
  const [siteArranging, setSiteArranging] = useState(false)
  const changeMapMode = useCallback((next: { site: boolean; groupId: number | null }) => {
    const value = { view: next.site ? 'site' as const : 'google' as const, groupId: next.groupId }
    saveMapMode(value)
    setMapMode(value)
  }, [])
  // #290 — read once each time the dashboard publishes its anchor, i.e. each time the map opens; #363 — and each
  // time the Google map comes back from the site map, which unmounted it.
  const savedCamera = useMemo(() => (anchor && !siteShown ? loadMapView().camera : null), [anchor, siteShown])
  const { t } = useTranslation('cameras')
  const queryClient = useQueryClient()
  // #279 — only a superuser may move a camera by dragging its pin (owner decision 2026-10-08).
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser === true)
  // #295 — only events that ring the bell make an alert notification, so the pin follows the same
  // decision as the alert card and the chime. #306 — a camera's pin alerts for 60 s from the arrival of
  // its latest alert (a new alert starts the 60 s again), and past that for as long as one of its
  // alert cards is still on screen (the mouse rests on it); "Clear all" empties the list and stops it.
  // The list changes on every drawer open, mark-read and dismiss; the markers are rebuilt (re-running
  // the clusterer and closing hover cards) only when the SET of alerting cameras shown on the map changes.
  const { notifications, peekIds } = useNotifications()
  // #294 redesign — a camera whose critical popup is on screen alerts its pin too (the popup replaces its toast),
  // for as long as the popup stays; outside the critical alerts provider this is empty.
  const { popups } = useCriticalAlertsContext()
  const popupCameras = new Set(popups.map((e) => e.camera).filter((id): id is number => typeof id === 'number'))

  const { data: mapPins = [] } = useQuery({
    queryKey: queryKeys.cameras.mapPins(),
    queryFn: () => camerasApi.getMapPins(),
    ...queryOptions.cameras.mapPins,
    // #336 (review I1) — this map is mounted on every signed-in page (MainLayout): its observer neither polls nor
    // refreshes on focus. The Dashboard's observer of the same query does both, once a minute, only on the Dashboard;
    // a live event and a reconnect still refresh the pins on any page (#247, useWebSocket).
    refetchInterval: false,
    refetchOnWindowFocus: false,
  })
  // #336 — a hover card's expired thumbnail refreshes the pins once (fresh links); every pin's link expires together
  const { onError: onThumbnailError } = useImageRetry(queryKeys.cameras.mapPins())
  // #247 — the page's filter bar narrows what is shown; null means everything.
  const shown = useMemo(() => (visibleIds ? mapPins.filter((c) => visibleIds.includes(c.id)) : mapPins), [mapPins, visibleIds])

  // #306 — bumped when a window ends (the timer below) or the page is shown again: the pins are worked out again
  const [rechecks, setRechecks] = useState(0)
  const { since, nextEnd } = alertingCameras(notifications, peekIds, Date.now())
  // 306 review — only the pins shown count: an alert for a camera that is not on the map, or that the
  // filter hides, leaves the key and the markers alone, when it arrives and when its 60 s end
  const alertingKey = shown.filter((c) => since.has(c.id) || popupCameras.has(c.id)).map((c) => c.id).sort((a, b) => a - b).join(',')
  // #306 — when each alerting camera's newest alert arrived: a new one bounces its pin again
  const alertStarts = Object.fromEntries(since) as Record<number, number>
  useEffect(() => {
    if (nextEnd === Infinity) return
    // One timeout to the next end, no polling. Set again after each firing (rechecks), so a timer that
    // fires a moment early is followed by another one.
    const timer = setTimeout(() => setRechecks((n) => n + 1), nextEnd - Date.now())
    return () => clearTimeout(timer)
  }, [nextEnd, rechecks])
  useEffect(() => {
    // 306 review — a hidden tab or a sleeping computer holds timers back; work the pins out again on return
    const onVisible = () => { if (document.visibilityState === 'visible') setRechecks((n) => n + 1) }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])
  const pulses = useMemo(() => {
    const ids: Record<number, true> = {}
    for (const id of alertingKey ? alertingKey.split(',') : []) ids[Number(id)] = true
    return ids
  }, [alertingKey])

  // #336 (review M1) — every pins refresh brings new thumbnail links (each API worker signs its own), and new marker
  // objects make the clusterer start again, which closes an open group card. A marker whose only change is its link
  // stays the same object and takes the new link (an open pin card shows it when the map draws again); when every
  // marker stays, the list stays too. Any other change (name, place, status, alert, last event) makes a new marker.
  const lastMarkersRef = useRef<MapMarker[]>([])
  const markers = useMemo(() => {
    const previous = lastMarkersRef.current
    const byId = new Map(previous.map((m) => [m.id, m]))
    const next = shown.map((c): MapMarker => {
      const fresh: MapMarker = {
        id: c.id,
        lat: parseFloat(c.latitude),
        lng: parseFloat(c.longitude),
        label: c.name,
        sublabel: c.location ?? undefined,
        address: c.location_address ?? null,
        thumbnailUrl: c.last_event_thumbnail_url ?? null,
        pulse: Boolean(pulses[c.id]),
        groupNames: c.group_names,
        isOnline: c.is_online,
        lastEventType: c.last_event_type,
        lastEventAt: c.last_event_at,
        // #330 — the facing wedge (absent from an older API: no wedge)
        heading: c.heading_degrees ?? null,
        fov: c.fov_degrees ?? null,
        rangeM: c.view_range_m ?? null,
      }
      const prev = byId.get(c.id)
      if (!prev || !sameExceptLink(prev, fresh)) return fresh
      prev.thumbnailUrl = fresh.thumbnailUrl
      return prev
    })
    const kept = next.length === previous.length && next.every((m, i) => m === previous[i])
    lastMarkersRef.current = kept ? previous : next
    return lastMarkersRef.current
  }, [shown, pulses])

  // #279 — a dropped pin asks here; the answer comes from the confirm box.
  const [pending, setPending] = useState<PendingMove | null>(null)
  const [saving, setSaving] = useState(false)
  const namesRef = useRef(new Map<number, string>())
  namesRef.current = new Map(mapPins.map((c) => [c.id, c.name]))
  const onMarkerMove = useCallback(
    (id: number, position: { lat: number; lng: number }) =>
      new Promise<boolean>((resolve) => {
        // A second drop while one is waiting refuses the first: one question at a time.
        setPending((prev) => { prev?.resolve(false); return { id, name: namesRef.current.get(id) ?? '', ...position, resolve } })
      }),
    [],
  )
  const answer = (accepted: boolean) => {
    setPending((prev) => { prev?.resolve(accepted); return null })
  }
  const confirmMove = async () => {
    if (!pending) return
    setSaving(true)
    try {
      await camerasApi.update(pending.id, { latitude: coord(pending.lat), longitude: coord(pending.lng) })
      // Pins, lists and the camera modal all show the position and the address: refresh them all.
      await queryClient.invalidateQueries({ queryKey: ['cameras'] })
      toast.success(t('map.move.moved'))
      answer(true)
    } catch {
      toast.error(t('map.move.failed'))
      answer(false)
    } finally {
      setSaving(false)
    }
  }

  // #330 — a superuser sets where a camera looks (owner decision 2026-10-13: super admin only, like #279).
  // "Set direction" on a pin starts the mode on that camera: the wedge follows the mouse (the draft), Escape
  // ends it, a click picks the heading and opens the confirm box. One camera at a time.
  const [directionMode, setDirectionMode] = useState<number | null>(null)
  const [directionDraft, setDirectionDraft] = useState<DirectionDraft | null>(null)
  const [directionBox, setDirectionBox] = useState<DirectionDraft | null>(null)
  const [savingDirection, setSavingDirection] = useState(false)
  // Round 2 — the values being edited in the confirm box (or in a camera's edit sheet), drawn live as a preview
  // wedge; while the box is open it wins over the picked heading. Cleared when the box closes.
  const preview = useDirectionPreview((s) => s.preview)
  const setPreview = useDirectionPreview((s) => s.set)
  const clearPreview = useDirectionPreview((s) => s.clear)
  const directionModeRef = useRef(directionMode)
  directionModeRef.current = directionMode
  // Fix round 1 (review M-2) — the mouse moves far more often than the screen draws: the newest heading waits in a
  // ref and is applied once per animation frame, and not at all when it has not changed. The draft goes to the
  // map's wedge layer only; the pins are memoised and are not redrawn for it.
  const pendingMove = useRef<DirectionDraft | null>(null)
  const moveFrame = useRef<number | null>(null)
  const dropPendingMove = useCallback(() => {
    if (moveFrame.current !== null) cancelAnimationFrame(moveFrame.current)
    moveFrame.current = null
    pendingMove.current = null
  }, [])
  useEffect(() => dropPendingMove, [dropPendingMove])   // no frame runs after the map is gone
  // The mouse stops being followed (Escape, a pick, another camera, the camera gone). The ref is cleared at once,
  // so a move that lands before the next render is ignored (review M-8).
  const stopFollowing = useCallback(() => {
    directionModeRef.current = null
    dropPendingMove()
    setDirectionMode(null)
  }, [dropPendingMove])
  const startDirection = useCallback((id: number) => {
    dropPendingMove()
    directionModeRef.current = id
    setDirectionMode(id); setDirectionDraft(null); setDirectionBox(null)
  }, [dropPendingMove])
  const moveDirection = useCallback((id: number, heading: number) => {
    if (directionModeRef.current !== id) return
    pendingMove.current = { id, heading }
    if (moveFrame.current !== null) return
    moveFrame.current = requestAnimationFrame(() => {
      moveFrame.current = null
      const next = pendingMove.current
      pendingMove.current = null
      if (!next || directionModeRef.current !== next.id) return
      setDirectionDraft((prev) => (prev?.id === next.id && prev.heading === next.heading ? prev : next))
    })
  }, [])
  const pickDirection = useCallback((id: number, heading: number) => {
    if (directionModeRef.current !== id) return
    stopFollowing()
    setDirectionDraft({ id, heading }); setDirectionBox({ id, heading })
  }, [stopFollowing])
  const endDirection = () => { stopFollowing(); setDirectionDraft(null); setDirectionBox(null); clearPreview() }
  useEffect(() => {
    if (directionMode === null) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { stopFollowing(); setDirectionDraft(null) } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [directionMode, stopFollowing])
  // Fix round 1 (review I-1) — the camera left the map (a filter edit, a status change on refresh): the mode ends
  // by itself, so nothing waits on a pin that is not there.
  useEffect(() => {
    if (directionMode !== null && !markers.some((m) => m.id === directionMode)) { stopFollowing(); setDirectionDraft(null) }
  }, [directionMode, markers, stopFollowing])
  // Leaving the dashboard ends a direction being set; the map opens without it next time. #363 — so does switching
  // to the site map, which takes the Google map away the same way.
  useEffect(() => {
    // round 2 — and its preview: the box goes with the map, and the next map must not open on a stale wedge
    if (!anchor || siteShown) { stopFollowing(); setDirectionDraft(null); setDirectionBox(null); clearPreview() }
  }, [anchor, siteShown, stopFollowing, clearPreview])
  const saveDirection = async (values: DirectionValues) => {
    if (!directionBox) return
    setSavingDirection(true)
    try {
      await camerasApi.update(directionBox.id, values)
      // The pins (and the camera lists and sheet) carry the direction: refresh them all, as #279 does.
      await queryClient.invalidateQueries({ queryKey: ['cameras'] })
      toast.success(t('map.direction.saved'))
    } catch {
      toast.error(t('map.direction.failed'))
    } finally {
      setSavingDirection(false)
      endDirection()
    }
  }
  const boxPin = directionBox ? mapPins.find((c) => c.id === directionBox.id) : undefined

  // No anchor = user is not on the dashboard. Return null: <MapView> unmounts and the library parks its Google map
  // (#369 reuseMaps); the next anchor mounts it again at the saved view (#290).
  if (!anchor) return null

  return (
    <>
      {createPortal(
        // #363 — the box: the Google map / Site map bar when a drawing exists, then the view shown. The same two
        // wrappers with or without the bar, so a drawing arriving never remounts the Google map.
        <div className="flex h-full w-full flex-col">
          {/* #368 — with the dashboard's title row published, the switch goes there (inline) and the box keeps its height;
              any other host, or no anchor yet, gets the bar in the box as before */}
          {sitePlans.length > 0 && (modeAnchor
            ? createPortal(
                <MapModeSwitch inline plans={sitePlans} site={siteShown} groupId={siteTarget} locked={siteShown && siteArranging} onChange={changeMapMode} />,
                modeAnchor,
              )
            : <MapModeSwitch plans={sitePlans} site={siteShown} groupId={siteTarget} locked={siteShown && siteArranging} onChange={changeMapMode} />
          )}
          <div className="min-h-0 w-full flex-1">
            {siteGroupId !== null ? (
              <SiteMapView
                key={siteGroupId}
                groupId={siteGroupId}
                pins={mapPins}
                visibleIds={visibleIds}
                pulses={pulses}
                alertStarts={alertStarts}
                selectedId={selectedId ?? null}
                onMarkerClick={onMarkerClick ?? undefined}
                onThumbnailError={onThumbnailError}
                onArrangingChange={setSiteArranging}
              />
            ) : (
              <MapView
                markers={markers}
                onMarkerClick={onMarkerClick ?? undefined}
                onMarkerMove={isSuperuser ? onMarkerMove : undefined}
                selectedId={selectedId ?? null}
                alertStarts={alertStarts}
                className="h-full w-full"
                cluster
                reuseMaps   // #369 — one Google map per dashboard visit: kept across leave/return and the Site map toggle
                // #287 — the map settings gear, superusers only (the API refuses anyone else as well)
                topLeftControl={isSuperuser ? <MapSettingsButton /> : undefined}
                initialView={savedCamera ?? undefined}
                fitSignal={filterEdits}
                // only once there are pins: the empty default view before they load is not worth keeping
                onViewChange={markers.length > 0 ? saveMapCamera : undefined}
                // #330 — superusers only, like the drag
                onSetDirection={isSuperuser ? startDirection : undefined}
                directionMode={isSuperuser ? directionMode : null}
                onDirectionMove={isSuperuser ? moveDirection : undefined}
                onDirectionPick={isSuperuser ? pickDirection : undefined}
                directionDraft={preview ?? directionDraft}   // round 2 — the values being edited win over the pick
                onThumbnailError={onThumbnailError}
              />
            )}
          </div>
        </div>,
        anchor,
      )}
      {directionBox && (
        <DirectionDialog
          key={directionBox.id}
          name={boxPin?.name ?? namesRef.current.get(directionBox.id) ?? ''}
          // #339 — the camera's own width and range, or empty fields that follow the map default
          initial={directionForm({ heading: directionBox.heading, fov: boxPin?.fov_degrees, range: boxPin?.view_range_m })}
          saving={savingDirection}
          onPreview={(v) => setPreview({ id: directionBox.id, heading: v.heading_degrees as number, fov: v.fov_degrees, rangeM: v.view_range_m })}
          onSave={(values) => { void saveDirection(values) }}
          onCancel={endDirection}
        />
      )}
      {pending && (
        <ConfirmDialog
          open
          onOpenChange={(open) => { if (!open) answer(false) }}
          variant="default"
          title={t('map.move.title')}
          description={t('map.move.description', { name: pending.name, lat: coord(pending.lat), lng: coord(pending.lng) })}
          confirmLabel={t('map.move.confirm')}
          loading={saving}
          onConfirm={() => { void confirmMove() }}
        />
      )}
    </>
  )
}
